import { action, computed, makeObservable, observable } from 'mobx';
import { UserProfileData } from '../feature-user-profile.types';

export type ContactLink = {
   type: string;
   link: string;
};

const EMPTY_ROW: ContactLink = { type: '', link: '' };
const MAX_CONTACT_LENGTH = 30;
const phonePattern = /^\+\d{1,3}\d{6,14}$/;
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CONTACT_LENGTH_ERROR = `Контакт не може перевищувати ${MAX_CONTACT_LENGTH} символів`;
const CONTACT_FORMAT_ERROR = 'Номер повинен бути у міжнародному форматі: +380XXXXXXXXX';
const EMAIL_REQUIRED_ERROR = "Електронна пошта є обов'язковою";
const EMAIL_FORMAT_ERROR = 'Некоректний формат електронної пошти';

const DEFAULT_MESSENGER_OPTIONS = ['Telegram', 'WhatsApp', 'Viber', 'Skype', 'Signal', 'Discord'];

const DEFAULT_SOCIAL_OPTIONS = ['Facebook', 'LinkedIn', 'X', 'Instagram', 'GitHub', 'TikTok'];

export class CandidateContactInfoStore {
   emailAddress = '';
   emailAddressError = '';
   contact = '';
   contactError = '';
   messengerRows: ContactLink[] = [{ ...EMPTY_ROW }];
   socialRows: ContactLink[] = [{ ...EMPTY_ROW }];
   messengerOptions: string[] = [...DEFAULT_MESSENGER_OPTIONS];
   socialOptions: string[] = [...DEFAULT_SOCIAL_OPTIONS];

   constructor() {
      makeObservable(this, {
         emailAddress: observable,
         emailAddressError: observable,
         contact: observable,
         contactError: observable,
         messengerRows: observable,
         socialRows: observable,
         messengerOptions: observable,
         socialOptions: observable,
         hydrate: action,
         setEmailAddress: action,
         setContact: action,
         updateMessengerRow: action,
         addMessengerRow: action,
         removeMessengerRow: action,
         updateSocialRow: action,
         addSocialRow: action,
         removeSocialRow: action,
         canAddMessengerRow: computed,
         canAddSocialRow: computed,
         maxMessengerRows: computed,
         maxSocialRows: computed,
         showEmailField: computed
      });
   }

   hydrate(profile: UserProfileData | null): void {
      if (!profile) {
         return;
      }
      this.emailAddress = profile.email_address ?? '';
      this.emailAddressError = this.validateEmail(this.emailAddress);
      this.contact = profile.contact ?? '';
      this.contactError = this.validateContact(this.contact);
      this.messengerOptions = profile.messengers?.length
         ? [...profile.messengers]
         : [...DEFAULT_MESSENGER_OPTIONS];
      this.socialOptions = profile.socials?.length
         ? [...profile.socials]
         : [...DEFAULT_SOCIAL_OPTIONS];
      this.messengerRows = this.ensureRows(profile.messenger_links);
      this.socialRows = this.ensureRows(profile.social_links);
      this.enforceRowsLimit();
   }

   setEmailAddress(value: string): void {
      this.emailAddress = value;
      this.emailAddressError = this.validateEmail(value);
   }

   setContact(value: string): void {
      this.contact = value;
      this.contactError = this.validateContact(value);
   }

   updateMessengerRow(index: number, key: keyof ContactLink, value: string): void {
      this.messengerRows = this.messengerRows.map((row, rowIndex) =>
         rowIndex === index ? { ...row, [key]: value } : row
      );
   }

   addMessengerRow(): void {
      if (!this.canAddMessengerRow) {
         return;
      }

      this.messengerRows = [...this.messengerRows, { ...EMPTY_ROW }];
   }

   removeMessengerRow(index: number): void {
      const nextRows = this.messengerRows.filter((_, rowIndex) => rowIndex !== index);
      this.messengerRows = nextRows.length ? nextRows : [{ ...EMPTY_ROW }];
   }

   updateSocialRow(index: number, key: keyof ContactLink, value: string): void {
      this.socialRows = this.socialRows.map((row, rowIndex) =>
         rowIndex === index ? { ...row, [key]: value } : row
      );
   }

   addSocialRow(): void {
      if (!this.canAddSocialRow) {
         return;
      }

      this.socialRows = [...this.socialRows, { ...EMPTY_ROW }];
   }

   removeSocialRow(index: number): void {
      const nextRows = this.socialRows.filter((_, rowIndex) => rowIndex !== index);
      this.socialRows = nextRows.length ? nextRows : [{ ...EMPTY_ROW }];
   }

   get canAddMessengerRow(): boolean {
      return this.messengerRows.length < this.maxMessengerRows;
   }

   get canAddSocialRow(): boolean {
      return this.socialRows.length < this.maxSocialRows;
   }

   get maxMessengerRows(): number {
      return Math.max(this.messengerOptions.length, 1);
   }

   get maxSocialRows(): number {
      return Math.max(this.socialOptions.length, 1);
   }

   get showEmailField(): boolean {
      if (this.emailAddress === null) {
         return true;
      }

      if (this.emailAddress.includes('@email.grc.ua')) {
         return false;
      } else {
         return true;
      }
   }

   getMessengerOptions(index: number): string[] {
      return this.getAvailableOptions(this.messengerOptions, this.messengerRows, index);
   }

   getSocialOptions(index: number): string[] {
      return this.getAvailableOptions(this.socialOptions, this.socialRows, index);
   }

   getMessengerFieldLabel(type: string): string {
      if (type === 'WhatsApp' || type === 'Viber' || type === 'Signal') {
         return 'Номер телефону';
      }
      if (type === 'Skype' || type === 'Discord') {
         return "Ім'я користувача";
      }
      return "Ім'я користувача або номер телефону";
   }

   getMessengerPlaceholder(type: string): string {
      if (type === 'WhatsApp' || type === 'Viber' || type === 'Signal') {
         return '+380XXXXXXXXX';
      }
      if (type === 'Skype' || type === 'Discord') {
         return "Ім'я користувача";
      }
      return "Ім'я користувача або номер телефону";
   }

   private getAvailableOptions(
      options: string[],
      rows: ContactLink[],
      currentIndex: number
   ): string[] {
      const currentValue = rows[currentIndex]?.type ?? '';
      const optionsPool = this.withCurrentOption(options, currentValue);

      const selectedValues = rows
         .map((row, index) => (index === currentIndex ? '' : row.type))
         .filter(Boolean);

      return optionsPool.filter((option) => !selectedValues.includes(option));
   }

   private withCurrentOption(options: string[], currentValue: string): string[] {
      if (!currentValue || options.includes(currentValue)) {
         return options;
      }
      return [...options, currentValue];
   }

   private ensureRows(rows: ContactLink[] | undefined): ContactLink[] {
      if (!rows || rows.length === 0) {
         return [{ ...EMPTY_ROW }];
      }

      return rows.map((row) => ({
         type: row.type ?? '',
         link: row.link ?? '',
      }));
   }

   private enforceRowsLimit(): void {
      if (this.messengerRows.length > this.maxMessengerRows) {
         this.messengerRows = this.messengerRows.slice(0, this.maxMessengerRows);
      }
      if (this.socialRows.length > this.maxSocialRows) {
         this.socialRows = this.socialRows.slice(0, this.maxSocialRows);
      }
   }

   private validateContact(value: string): string {
      if (!value) {
         return '';
      }
      if (value.length > MAX_CONTACT_LENGTH) {
         return CONTACT_LENGTH_ERROR;
      }
      if (!phonePattern.test(value)) {
         return CONTACT_FORMAT_ERROR;
      }
      return '';
   }

   private validateEmail(value: string): string {
      if (!value.trim()) {
         return EMAIL_REQUIRED_ERROR;
      }
      if (!emailPattern.test(value)) {
         return EMAIL_FORMAT_ERROR;
      }
      return '';
   }
}
